import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/src/shadComponents/ui/dialog';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import { CreateDeviceRegistrationRequest } from './hooks/useDeviceRegistrationAutoenroll';

interface DeviceRegistrationCreateModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDeviceCreated: () => void;
    onCreateDevice: (device: CreateDeviceRegistrationRequest) => Promise<void>;
}

export default function DeviceRegistrationCreateModal({
    open,
    onOpenChange,
    onDeviceCreated,
    onCreateDevice,
}: DeviceRegistrationCreateModalProps) {
    const [formData, setFormData] = useState({
        deviceId: '',
        deviceName: '',
        groupNames: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.deviceId.trim()) {
            newErrors.deviceId = 'Device ID is required';
        }

        if (!formData.deviceName.trim()) {
            newErrors.deviceName = 'Device Name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const deviceToCreate: CreateDeviceRegistrationRequest = {
                deviceId: formData.deviceId.trim(),
                deviceName: formData.deviceName.trim(),
                groupNames: formData.groupNames.trim() || undefined,
            };

            await onCreateDevice(deviceToCreate);
            onDeviceCreated();
            onOpenChange(false);
            resetForm();
        } catch (error) {
            console.error('Error creating device registration:', error);
            // Error handling is done in parent component
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const resetForm = () => {
        setFormData({
            deviceId: '',
            deviceName: '',
            groupNames: '',
        });
        setErrors({});
    };

    const handleClose = (open: boolean) => {
        if (!isSubmitting) {
            onOpenChange(open);
            if (!open) {
                resetForm();
            }
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Add Device Registration</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="deviceId">Device ID *</Label>
                        <Input
                            id="deviceId"
                            name="deviceId"
                            value={formData.deviceId}
                            onChange={handleChange}
                            placeholder="Enter unique device identifier"
                            disabled={isSubmitting}
                            className={errors.deviceId ? 'border-red-500' : ''}
                        />
                        {errors.deviceId && (
                            <p className="text-sm text-red-500">
                                {errors.deviceId}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="deviceName">Device Name *</Label>
                        <Input
                            id="deviceName"
                            name="deviceName"
                            value={formData.deviceName}
                            onChange={handleChange}
                            placeholder="Enter descriptive device name"
                            disabled={isSubmitting}
                            className={
                                errors.deviceName ? 'border-red-500' : ''
                            }
                        />
                        {errors.deviceName && (
                            <p className="text-sm text-red-500">
                                {errors.deviceName}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="groupNames">
                            Group Names (optional)
                        </Label>
                        <Textarea
                            id="groupNames"
                            name="groupNames"
                            value={formData.groupNames}
                            onChange={handleChange}
                            placeholder="Enter group names separated by commas (e.g., Security, Main Floor, Night Shift)"
                            rows={3}
                            disabled={isSubmitting}
                        />
                        <p className="text-xs text-muted-foreground">
                            Separate multiple group names with commas. Groups
                            will be created automatically if they don't exist.
                        </p>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleClose(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create Device'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
